import {formatDistanceToNow} from 'date-fns';
import {CheckCircle2, ChevronDown, Film, ShieldCheck, XCircle} from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {type QcReviewItem} from '@/api/qcReview';
import {useApproveQcReview} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {SensitiveMediaButton} from '@/components/shared/SensitiveMedia';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {showToast} from '@/lib/toast';

const COLUMN_COUNT = 9;

export function QcQueueTable({
  items,
  isLoading,
  onOpenReview,
  onEditAndApprove,
  selectedIds,
  onToggleRow,
  onToggleAll
}: {
  readonly items: QcReviewItem[];
  readonly isLoading: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggleRow: (id: string) => void;
  readonly onToggleAll: () => void;
  /**
   Opens the detail review modal (video preview, full context, approve/reject inside) - triggered
   by clicking the item itself or the row's Reject button, same as Weekly QA's queue table.
   */
  readonly onOpenReview: (item: QcReviewItem) => void;
  /** Opens the same modal, but straight into the correction picker. */
  readonly onEditAndApprove: (item: QcReviewItem) => void;
}) {
  const approve = useApproveQcReview();
  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));

  async function handleApprove(item: QcReviewItem) {
    try {
      await approve.mutateAsync({id: item.id, payload: {}});
      showToast('Approved.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to approve item.'), 'error');
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-6">
            <Checkbox
              aria-label={allSelected ? 'Clear selection' : 'Select all loaded items'}
              checked={allSelected}
              indeterminate={selectedIds.size > 0 && !allSelected}
              disabled={items.length === 0}
              onCheckedChange={() => {
                onToggleAll();
              }}
            />
          </TableHead>
          {/* The Submissions grid's columns and order. Delivery Status and Status Check are left out:
              everything here is held before delivery, so neither has a value yet. */}
          <TableHead>Content</TableHead>
          <TableHead>Policy / Risks</TableHead>
          <TableHead>Language / Location</TableHead>
          <TableHead>Topic</TableHead>
          <TableHead>Notes</TableHead>
          <TableHead>Analyst</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="bg-card sticky right-0 text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                <Spinner />
                Loading…
              </div>
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && items.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="p-0">
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ShieldCheck />
                  </EmptyMedia>
                  <EmptyTitle>Nothing held for review</EmptyTitle>
                  <EmptyDescription>
                    No items waiting on QC review for this policy violation.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </TableCell>
          </TableRow>
        ) : null}
        {items.map((item) => {
          const title = item.title ?? item.item_external_id ?? item.id;
          return (
            <TableRow key={item.id} data-state={selectedIds.has(item.id) ? 'selected' : undefined}>
              <TableCell>
                <Checkbox
                  aria-label={`Select ${title}`}
                  checked={selectedIds.has(item.id)}
                  onCheckedChange={() => {
                    onToggleRow(item.id);
                  }}
                />
              </TableCell>
              <TableCell className="max-w-[320px]">
                <div className="flex items-center gap-3">
                  {item.thumbnail_url ? (
                    <SensitiveMediaButton
                      compact
                      label={`Review ${title}`}
                      src={item.thumbnail_url}
                      alt=""
                      className="bg-muted h-9 w-14 shrink-0 overflow-hidden"
                      imageClassName="h-full w-full object-cover"
                      onClick={() => {
                        onOpenReview(item);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      aria-label={`Review ${title}`}
                      className="bg-muted flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden"
                      onClick={() => {
                        onOpenReview(item);
                      }}>
                      <Film className="text-muted-foreground size-4" />
                    </button>
                  )}
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="hover:text-primary-ink block max-w-full truncate text-left text-xs font-medium transition-colors"
                      onClick={() => {
                        onOpenReview(item);
                      }}>
                      {title}
                    </button>
                    {item.title && item.item_external_id ? (
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {item.item_external_id}
                      </p>
                    ) : null}
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-[200px]">
                <div className="flex flex-col items-start gap-1">
                  <Badge variant="outline">{item.policy_violation}</Badge>
                  <TruncatedText className="text-muted-foreground max-w-full text-xs">
                    {item.submission.risks.join(', ') || item.original_risk}
                  </TruncatedText>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[160px]">
                <TruncatedText>{item.submission.language.join(', ') || '—'}</TruncatedText>
                <TruncatedText className="text-xs">
                  {item.submission.location.join(', ') || '—'}
                </TruncatedText>
              </TableCell>
              <TableCell className="max-w-[160px] text-xs">
                <TruncatedText>{item.submission.topic.join(', ') || '—'}</TruncatedText>
              </TableCell>
              <TableCell className="max-w-[280px]">
                {item.submission.notes ? (
                  <TruncatedText>{item.submission.notes}</TruncatedText>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[180px] text-xs">
                <TruncatedText>{item.analyst_email}</TruncatedText>
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {formatDistanceToNow(new Date(item.created_at), {addSuffix: true})}
              </TableCell>
              <TableCell className="bg-card sticky right-0 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    disabled={approve.isPending}
                    onClick={() => void handleApprove(item)}>
                    {approve.isPending ? <Spinner /> : <CheckCircle2 />}
                    Approve
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={approve.isPending}
                    onClick={() => {
                      onOpenReview(item);
                    }}>
                    <XCircle />
                    Reject
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      disabled={approve.isPending}
                      render={
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="secondary"
                          aria-label="More actions"
                        />
                      }>
                      <ChevronDown />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() => {
                          onEditAndApprove(item);
                        }}>
                        Approve with edits…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
